
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Helmet } from "react-helmet";
import { useCatalogs, useCatalogPages } from "@/hooks/useCatalogs";
import { AboutCreator } from "@/components/AboutCreator";
import { useAdmin } from "@/providers/AdminProvider";
import { CreateCatalogDialog } from "@/components/CreateCatalogDialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

const CatalogCard = ({ catalog, onClick }: { catalog: { id: string; title: string }; onClick: () => void }) => {
  const { data: pages, error: pagesError } = useCatalogPages(catalog.id);
  
  // If there's an error loading pages, log it but don't break the component
  if (pagesError) {
    console.error('Error loading catalog pages:', pagesError);
  }
  
  const firstPage = pages?.[0];

  return (
    <Card
      className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
      onClick={onClick}
    >
      <div className="aspect-[8.5/11] relative">
        {firstPage?.thumbnail_url ? (
          <img
            src={firstPage.thumbnail_url}
            alt={catalog.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <p className="text-muted-foreground">No preview available</p>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-medium text-center">{catalog.title}</h3>
      </div>
    </Card>
  );
};

const CatalogsPage = () => {
  const navigate = useNavigate();
  const { data: catalogs, isLoading, error } = useCatalogs();
  const { isAdmin } = useAdmin();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // If there's an error, log it but don't break the page
  if (error) {
    console.error('Error loading catalogs:', error);
  }

  return (
    <>
      <Helmet>
        <title>Vintage Coach Catalogs - View PDFs of Old Coach Catalogs</title>
        <meta name="description" content="Browse vintage Coach catalogs featuring classic Coach bags and accessories. View original catalog pages and discover classic Coach designs through the years." />
      </Helmet>
      <main className="min-h-screen w-full flex flex-col">
        <div className="flex-grow max-w-6xl mx-auto px-4 pt-20 pb-12">
          <Header />

          <div className="space-y-8 mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-display">Available Catalogs</h2>
              {isAdmin && (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Catalog
                </Button>
              )}
            </div>
            
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="h-72 animate-pulse bg-muted" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {catalogs?.map((catalog) => (
                  <CatalogCard
                    key={catalog.id}
                    catalog={catalog}
                    onClick={() => navigate(`/catalogs/${catalog.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <AboutCreator />
      </main>

      {isAdmin && (
        <CreateCatalogDialog 
          isOpen={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
        />
      )}
    </>
  );
};

export default CatalogsPage;
